import { useState, useCallback } from 'react';

interface CepData {
  logradouro: string;
  bairro: string;
  localidade: string;
  uf: string;
  erro?: boolean;
}

interface UseCepLookupOptions {
  onSuccess?: (data: CepData) => void;
  onError?: (error: Error) => void;
}

interface UseCepLookupReturn {
  isLoading: boolean;
  fetchCep: (cep: string) => Promise<CepData | null>;
}

/**
 * Hook para buscar dados de endereço via CEP (ViaCEP API)
 *
 * @example
 * // Uso básico com callback
 * const { fetchCep, isLoading } = useCepLookup({
 *   onSuccess: (data) => {
 *     setValue('logradouro', data.logradouro);
 *     setValue('bairro', data.bairro);
 *     setValue('cidade', data.localidade);
 *     setValue('uf', data.uf);
 *   }
 * });
 *
 * @example
 * // Uso com await
 * const { fetchCep } = useCepLookup();
 * const data = await fetchCep('01310-100');
 * if (data) {
 *   console.log(data.localidade); // São Paulo
 * }
 */
export function useCepLookup(options?: UseCepLookupOptions): UseCepLookupReturn {
  const [isLoading, setIsLoading] = useState(false);

  const fetchCep = useCallback(async (cep: string): Promise<CepData | null> => {
    const cleanCep = cep.replace(/\D/g, '');

    if (cleanCep.length !== 8) {
      return null;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data: CepData = await response.json();

      if (data.erro) {
        const error = new Error('CEP não encontrado');
        options?.onError?.(error);
        return null;
      }

      options?.onSuccess?.(data);
      return data;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Erro ao buscar CEP');
      options?.onError?.(error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [options]);

  return { isLoading, fetchCep };
}
